<template>
  <v-alert
    v-bind="$attrs"
    :type="type"
    border="start"
    variant="tonal"
    class="celus-alert"
    :class="{ 'celus-alert--container': hasContainer }"
    :style="{
      '--celus-alert-color': `rgb(var(--v-theme-${type}))`,
      '--celus-alert-container': `rgb(var(--v-theme-${type}-container))`,
      '--celus-alert-on-container': `rgb(var(--v-theme-on-${type}-container))`,
      '--celus-alert-on-color': `rgb(var(--v-theme-on-${type}))`,
    }"
  >
    <slot />
  </v-alert>
</template>

<script setup lang="ts">
import { computed, type PropType } from "vue";
import { useTheme } from "vuetify";

type AlertType = "info" | "warning" | "error" | "success";

defineOptions({
  name: "CelusAlert",
  inheritAttrs: false,
});

const props = defineProps({
  type: {
    type: String as PropType<AlertType>,
    required: true,
    validator: (value: string) =>
      ["info", "warning", "error", "success"].includes(value),
  },
});

const theme = useTheme();

// Alerts are painted with the type's container pair (e.g. warning-container
// + on-warning-container) whenever the theme defines it, unless the theme opts
// into the tinted look below with the `alert-style: "tint"` theme variable.
const hasContainer = computed(() => {
  const { colors, variables } = theme.current.value;
  return (
    variables["alert-style"] !== "tint" &&
    `${props.type}-container` in colors &&
    `on-${props.type}-container` in colors
  );
});
</script>

<style scoped>
/* Vuetify's tonal variant leaves the root transparent and paints the color as a
   translucent underlay, so the page background bleeds through and shifts the
   hue on grey. An opaque surface underneath pins it to the theme surface.
   The tint strength and text mix direction come from the theme variables
   (plugins/vuetify.js), as light and dark themes need opposite adjustments. */
.celus-alert.v-alert--variant-tonal {
  --celus-alert-tint-opacity: var(--v-alert-tint-opacity, 0.2);
  --celus-alert-text-mix: 60%;

  background-color: rgb(var(--v-theme-surface));
}

/* Vuetify paints the text in the undiluted type color, which only reaches ~2.5:1
   against the tinted background. Mixing toward the theme's text mix target
   (black on light themes, white on dark ones) keeps the hue while pushing the
   contrast up. Only the content is mixed, so the icon and border keep the
   vivid color. */
.celus-alert.v-alert--variant-tonal :deep(.v-alert__content) {
  --celus-alert-text-color: color-mix(
    in srgb,
    var(--celus-alert-color) var(--celus-alert-text-mix),
    var(--v-alert-text-mix-target, black)
  );

  color: var(--celus-alert-color);
  color: var(--celus-alert-text-color);
}

.celus-alert.v-alert--variant-tonal :deep(.v-btn.v-btn--variant-flat) {
  background-color: var(--celus-alert-color);
  color: rgb(var(--v-theme-surface));
}

/* Theme `anchor` is primary teal; it clashes on info/error/warning tints.
   Keep links in the darkened type color and underline them. */
.celus-alert.v-alert--variant-tonal :deep(.v-alert__content a) {
  color: var(--celus-alert-text-color);
  text-decoration: underline;
}

/* The underlay carries the type color as currentColor. Vuetify tints it at
   --v-activated-opacity (0.12), which reads too pale for a full-width block. */
.celus-alert.v-alert--variant-tonal :deep(.v-alert__underlay) {
  opacity: var(--celus-alert-tint-opacity);
}

/* Vuetify draws the start bar in the type color at 0.38 opacity, a much softer
   shade than the icon; raise it so the bar reads close to the icon color. */
.celus-alert :deep(.v-alert__border) {
  opacity: 0.6;
}

/* Container look: an opaque type container fill with on-container text, while
   the start bar and icon keep the full type color. */
.celus-alert.celus-alert--container.v-alert--variant-tonal {
  background-color: var(--celus-alert-container);
}

.celus-alert.celus-alert--container.v-alert--variant-tonal
  :deep(.v-alert__underlay) {
  opacity: 0;
}

.celus-alert.celus-alert--container.v-alert--variant-tonal
  :deep(.v-alert__content) {
  --celus-alert-text-color: var(--celus-alert-on-container);

  color: var(--celus-alert-on-container);
}

.celus-alert.celus-alert--container.v-alert--variant-tonal
  :deep(.v-btn.v-btn--variant-flat) {
  color: var(--celus-alert-on-color);
}
</style>
